'use client';

import { useState, useEffect } from 'react';
import { Wrench, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/providers/I18nProvider';
import { ToolsList } from './ToolsList';
import { MCPServersList } from './MCPServersList';
import { AddMCPServerModal } from './AddMCPServerModal';

interface ToolsSettingsProps {
  agentId: string;
  source?: 'chat-admin' | 'widget-admin';
}

export function ToolsSettings({ agentId, source = 'widget-admin' }: ToolsSettingsProps) {
  const { agent, isLoading, updateAgent } = useAgent(agentId);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [showAddMCPServer, setShowAddMCPServer] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [loadingTools, setLoadingTools] = useState(true);
  const [availableTools, setAvailableTools] = useState<any[]>([]);
  const [mcpServers, setMCPServers] = useState<any[]>([]);
  const [hasSetDefaults, setHasSetDefaults] = useState(false);

  // Load selected tools from agent config
  useEffect(() => {
    if (!agent) return;
    
    // If enabledTools is explicitly set (even if empty), use it
    if (agent.configDoc?.enabledTools !== undefined) {
      setSelectedTools(agent.configDoc.enabledTools);
    }
  }, [agent]);

  // Set default selections after tools are loaded
  useEffect(() => {
    // Only run if:
    // 1. Agent is loaded
    // 2. Tools are loaded
    // 3. No tools are currently selected
    // 4. enabledTools was empty/undefined in the agent config
    // 5. We haven't already set defaults
    if (!agent || loadingTools || hasSetDefaults) return;
    
    const hasNoConfiguredTools = !agent.configDoc?.enabledTools || agent.configDoc.enabledTools.length === 0;
    const hasVectorizeTools = availableTools.some(t => t.functionType === 'vectorize-retrieve');
    
    if (hasNoConfiguredTools && hasVectorizeTools && selectedTools.length === 0) {
      // Find all vectorize tools and enable them by default
      const vectorizeTools = availableTools.filter(t => t.functionType === 'vectorize-retrieve');
      if (vectorizeTools.length > 0) {
        const toolIds = vectorizeTools.map(t => t.id);
        setSelectedTools(toolIds);
        setHasSetDefaults(true);

        // Save this as the default
        updateAgent.mutate({
          enabledTools: toolIds,
        });
      }
    }
  }, [agent, availableTools, loadingTools, selectedTools, hasSetDefaults, updateAgent]);

  // Load available tools and MCP servers
  useEffect(() => {
    loadToolsAndServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);
  
  // Handle OAuth callback messages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'success') {
      showToast(t('admin.tools.oauth.success'), 'success');
      // Remove OAuth params but keep section
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth');
      url.searchParams.delete('serverId');
      window.history.replaceState({}, document.title, url.toString());
    } else if (params.get('error')?.startsWith('oauth_')) {
      const error = params.get('error')?.replace('oauth_', '').replace(/_/g, ' ') || 'Unknown error';
      showToast(t('admin.tools.oauth.error', { error }), 'error');
      // Remove error param but keep section
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [showToast, t]);

  const loadToolsAndServers = async () => {
    try {
      setLoadingTools(true);
      
      // Load agent's custom functions
      const functionsResponse = await fetch(`/api/agents/${agentId}/custom-functions`);
      if (functionsResponse.ok) {
        const { customFunctions } = await functionsResponse.json();
        setAvailableTools(customFunctions || []);
      } else {
        console.error('[ToolsSettings] Failed to load custom functions:', functionsResponse.status);
      }

      // Load MCP servers for the agent
      const serversResponse = await fetch(`/api/agents/${agentId}/mcp-servers`);
      if (serversResponse.ok) {
        const { servers } = await serversResponse.json();
        setMCPServers(servers || []);
      }
    } catch (error) {
      console.error('Failed to load tools and servers:', error);
      showToast(t('admin.tools.errors.loadFailed'), 'error');
    } finally {
      setLoadingTools(false);
    }
  };

  const handleToolToggle = async (toolId: string, enabled: boolean) => {
    const updatedTools = enabled
      ? [...selectedTools, toolId]
      : selectedTools.filter(id => id !== toolId);

    setSelectedTools(updatedTools);

    try {
      await updateAgent.mutateAsync({
        enabledTools: updatedTools,
      });
      showToast(t('admin.tools.saveSuccess'), 'success');
    } catch (error) {
      console.error('Failed to update tools:', error);
      showToast(t('admin.tools.errors.saveFailed'), 'error');
      // Revert on error
      setSelectedTools(selectedTools);
    }
  };

  const handleMCPServerAdded = () => {
    loadToolsAndServers();
    setShowAddMCPServer(false);
  };

  const handleMCPServerDeleted = (serverId: string) => {
    setMCPServers(servers => servers.filter(s => s.id !== serverId));
  };

  const handleBatchToolToggle = async (toolIds: string[], enable: boolean) => {
    const updatedTools = enable
      ? [...new Set([...selectedTools, ...toolIds])] // Add all tools (using Set to avoid duplicates)
      : selectedTools.filter(id => !toolIds.includes(id)); // Remove specified tools

    setSelectedTools(updatedTools);

    try {
      await updateAgent.mutateAsync({
        enabledTools: updatedTools,
      });
      showToast(t(`admin.tools.${enable ? 'allEnabled' : 'allDisabled'}`), 'success');
    } catch (error) {
      console.error('Failed to update tools:', error);
      showToast(t('admin.tools.errors.saveFailed'), 'error');
      // Revert on error
      setSelectedTools(selectedTools);
    }
  };

  if (isLoading || loadingTools) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Info box skeleton */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex">
              <div className="h-5 w-5 bg-blue-300 dark:bg-blue-600 rounded animate-pulse flex-shrink-0"></div>
              <div className="ml-3 flex-1">
                <div className="h-4 w-32 bg-blue-300 dark:bg-blue-600 rounded animate-pulse mb-2"></div>
                <div className="h-4 w-full bg-blue-200 dark:bg-blue-700 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Settings sections skeleton */}
          <div className="space-y-8 mt-8">

            {/* Vectorize Tools Skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-80 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {[1, 2].map(i => (
              <div key={i} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
                  <div className="h-4 w-full max-w-xl bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
                <div className="h-5 w-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

            {/* MCP Servers Skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
            <div>
              <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
              <div className="h-4 w-72 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="h-9 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {[1, 2].map(i => (
              <div key={i} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-5 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
                  <div className="h-4 w-full max-w-md bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
                <div className="h-5 w-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.tools.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.tools.description')}
          </p>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-blue-400 dark:text-blue-300 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">{t('admin.tools.info.title')}</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                {t('admin.tools.info.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Settings sections */}
        <div className="space-y-8 mt-8">

          {/* Vectorize Tools Section */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.tools.vectorize.title')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.tools.vectorize.description')}</p>
            </div>
            <ToolsList
              tools={availableTools.filter(t => t.functionType === 'vectorize-retrieve')}
              selectedTools={selectedTools}
              onToggle={handleToolToggle}
            />
          </section>

          {/* MCP Servers Section */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.tools.mcp.title')}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.tools.mcp.description')}</p>
              </div>
              <button
                onClick={() => setShowAddMCPServer(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('admin.tools.mcp.addButton')}
              </button>
            </div>
            <MCPServersList
              servers={mcpServers}
              availableTools={availableTools.filter(t => t.functionType === 'MCP_EXTERNAL')}
              selectedTools={selectedTools}
              onToolToggle={handleToolToggle}
              onBatchToolToggle={handleBatchToolToggle}
              onServerDeleted={handleMCPServerDeleted}
              source={source}
              onRefresh={loadToolsAndServers}
              agentId={agentId}
            />
          </section>
        </div>
      </div>

      {/* Add MCP Server Modal */}
      {showAddMCPServer && (
        <AddMCPServerModal
          agentId={agentId}
          onClose={() => setShowAddMCPServer(false)}
          onServerAdded={handleMCPServerAdded}
          source={source}
        />
      )}
    </div>
  );
}